<template>
  <div
    class="flex min-w-min max-w-max items-end justify-end button-primary-fg rounded-[36px]"
    :class="{ 'bg-utility-brand-700_alt': type }"
  >
    <Component
      v-if="type && type !== 'recurring_task' && action.hasPermission"
      :is="action.component"
    >
      <Button
        variant="default"
        :label="action.label"
        size="2xl"
        class="flex h-14 p-4 pl-5 items-center justify-end gap-2.5 rounded-r-0 shadow-3xl"
        :aria-label="action.ariaLabel"
      >
        <template #start>
          <Icon name="plus" aria-hidden size="xl" />
        </template>
      </Button>
    </Component>

    <RecurringTaskButton v-if="type === 'recurring_task'" />

    <IconButton
      variant="default"
      size="2xl"
      icon
      class="flex w-14 h-14 p-4 items-center justify-end bg-utility-brand-800_alt !border-utility-brand-800_alt aspect-square rounded-[40px] shadow-3xl"
      :aria-label="$t('components.buttons.scan_qr_code')"
      @click.stop="handleScanQrCode()"
    >
      <Icon name="qr-code" class="button-primary-fg" aria-hidden size="full" />
    </IconButton>

    <!-- Modal: Scan QR -->
    <ScanQrModal v-model:open="openQrScanner" @decode="handleQrCodeScanned" />

    <!-- Modal: Assign QR Code -->
    <AssignQrCodeDrawer
      :open="openAssignQrCode"
      :buildings="buildings"
      :buildingId="buildingId"
      @assign="handleAssignSpace"
      @close="handleCloseAssignQrCode"
    />

    <AssetDetail
      v-if="openAssetDetail"
      :asset="details"
      @reassign="openAssignQrCode = true"
      @createTask="handleAddTask"
      @close="openAssetDetail = false"
    />

    <SpaceDetail
      v-if="openSpaceDetail"
      :space="details"
      @reassign="openAssignQrCode = true"
      @createTask="handleAddTask"
      @close="openSpaceDetail = false"
    />
  </div>
</template>

<script setup>
import { computed, inject, onMounted, onUnmounted, ref, watch } from "vue";

// Import Utilities
import axios from "axios";
import route from "ziggy-js";
import { router, usePage } from "@inertiajs/vue3";
import { storeToRefs } from "pinia";
import { isEmpty } from "lodash";
import { useI18n } from "vue-i18n";

// Import Components
import { default as AssetDetail } from "@/Pages/Assets/Detail.vue";
import { default as SpaceDetail } from "@/Pages/Spaces/Detail.vue";
import ScanQrModal from "@/Tailwind/Components/ScanQrModal.vue";
import AssignQrCodeDrawer from "@/Tailwind/Components/AssignQrCodeDrawer.vue";
import { TaskCreate } from "@/SharedComponents/TaskCreate";
import { FilesUploadDialog } from "@/SharedComponents/FilesUploadDialog";
import { Modal as AssetCreateModal } from "@/Pages/Assets/Partials/Create";
import { Modal as AssetGroupCreateModal } from "@/Pages/AssetGroups/Partials/Create";
import { Modal as SpaceCreateModal } from "@/Pages/Spaces/Partials/Create";
import RecurringTaskButton from "./RecurringTaskButton.vue";
import { Button, IconButton } from "@/SharedComponents/Buttons";
import { Icon } from "@/SharedComponents/Icon";
import { toast } from "@/SharedComponents/Toaster";

// Import Composables
import { useFilters } from "@/Composables/useFilters";
import { useSites } from "@/Composables/useSites";
import { useQrCodeScanner } from "@/Composables/useQrCodeScanner";

// Translations
const { t } = useI18n();

// Sites
const { isLoading, data: buildings } = useSites();

// Permissions
const { hasPermission, permissions } = inject("permissions");

// Filters: Global
const globalFilterStore = useFilters("global");
const { filterValues } = storeToRefs(globalFilterStore);

const {
  runningInWebView,
  getWebViewBridge,
  verifyQrCode,
  openQrCodeScanner,
  dismissQrCodeScanner,
} = useQrCodeScanner();

const page = usePage();

onMounted(() => {
  if (runningInWebView()) {
    getWebViewBridge().addEventListener(
      "onQrCodeScanned",
      handleWebviewQrCodeScanned
    );
  }
});

onUnmounted(() => {
  if (runningInWebView()) {
    getWebViewBridge().removeEventListener(
      "onQrCodeScanned",
      handleWebviewQrCodeScanned
    );
  }
});

// Define Props
const props = defineProps({
  type: { type: String, required: false },
});

// Refs
const openQrScanner = ref(false);
const openSpaceDetail = ref(false);
const openAssignQrCode = ref(false);
const openAssetDetail = ref(false);
const details = ref(undefined);
const QRCode = ref(null);

// Computed Props
const buildingId = computed(() => {
  return filterValues.value.tenant ? filterValues.value.tenant[0] : null;
});

const action = computed(() => {
  switch (props.type) {
    case "task":
      return {
        label: t("admin.tasks.label"),
        ariaLabel: t("components.buttons.add_new_problem"),
        component: TaskCreate,
        hasPermission: true,
      };
    case "file":
      return {
        label: t("admin.files.label", 2),
        ariaLabel: t("components.buttons.add_new_file"),
        component: FilesUploadDialog,
        hasPermission: canCreateFiles.value,
      };
    case "asset":
      return {
        label: t("admin.assets.label", 1),
        ariaLabel: t("components.buttons.add_new_asset"),
        component: AssetCreateModal,
        hasPermission: canCreateAssets.value,
      };
    case "asset_group":
      return {
        label: t("admin.asset_groups.label", 1),
        ariaLabel: t("components.buttons.add_new_asset_group"),
        component: AssetGroupCreateModal,
        hasPermission: canCreateAssets.value,
      };
    case "space":
      return {
        label: t("admin.spaces.label", 1),
        ariaLabel: t("components.buttons.add_new_space"),
        component: SpaceCreateModal,
        hasPermission: canCreateSpaces.value,
      };
    case "recurring_task":
      return {
        label: t("admin.recurring_tasks.label", 1),
        ariaLabel: t("components.buttons.add_new_recurring_task"),
        component: undefined,
        hasPermission: canCreateRecurringTasks.value,
      };
    default:
      return {};
  }
});

const filteredSites = computed(() => {
  if (isLoading.value) {
    return [];
  }

  return buildings.value.filter((building) =>
    hasPermission(permissions.createDocument, building.id)
  );
});

const canCreateFiles = computed(() => {
  if (!buildingId.value) {
    return filteredSites.value.length !== 0;
  }
  return hasPermission(permissions.createDocument, buildingId.value);
});

const canCreateAssets = computed(() => {
  if (!buildingId.value) {
    return filteredSites.value.length !== 0;
  }
  return hasPermission(permissions.createAsset, buildingId.value);
});

const canCreateSpaces = computed(() => {
  if (!buildingId.value) {
    return false;
  }
  return hasPermission(permissions.createSpace, buildingId.value);
});

const canCreateRecurringTasks = computed(() => {
  if (isLoading.value) {
    return false;
  }

  return !isEmpty(
    buildings.value.filter((building) =>
      hasPermission(permissions.createRecurringTask, building.id)
    )
  );
});

// Functions
async function handleWebviewQrCodeScanned(e) {
  dismissQrCodeScanner();

  const { id } = e.detail;
  await handleQrCodeScanned(id);
}

async function handleQrCodeScanned(id) {
  const { assigned, space, uuid } = await verifyQrCode(id);

  openQrScanner.value = false;
  QRCode.value = uuid;

  if (assigned) {
    axios
      .post(route("api.space.info"), {
        space_id: space.id,
      })
      .then((response) => {
        details.value = { ...response.data.data, scanned: true };
        if (space.type.value === "asset") {
          openAssetDetail.value = true;
        } else {
          openSpaceDetail.value = true;
        }
      });
  } else {
    handleAssignQrCode();
  }
}

function handleScanQrCode() {
  // If a native QR code scanner is available, use it.
  if (runningInWebView()) {
    openQrCodeScanner();
  } else {
    openQrScanner.value = true;
  }
}

const handleCloseAssignQrCode = () => {
  openAssignQrCode.value = false;
  axios.post(route("qr_code.cancel"));
};

function handleAssignQrCode() {
  openAssignQrCode.value = true;
}

function handleAddTask(value) {
  router.visit(route("tasks.add"), {
    data: {
      spaceId: value?.spaceId,
      buildingId: value?.buildingId,
      redirect: true,
    },
  });
}

function handleAssignSpace(id) {
  openAssignQrCode.value = false;

  router.put(
    route("qr_code.update", QRCode.value),
    { space_id: id },
    {
      onFinish: () => {
        QRCode.value = null;
        toast.success(t("toasts.qr_code.assign_space.success"));
      },
    }
  );
}

const item = computed(() => {
  switch (props.type) {
    case "task":
      return page.props.task;
    case "file":
      return page.props.file;
    case "asset":
      return page.props.asset;
    case "asset_group":
      return page.props.asset_group;
    default:
      return null;
  }
});

if (runningInWebView()) {
  watch(
    item,
    () => {
      if (item.value) {
        getWebViewBridge().removeEventListener(
          "onQrCodeScanned",
          handleWebviewQrCodeScanned
        );
      } else {
        getWebViewBridge().addEventListener(
          "onQrCodeScanned",
          handleWebviewQrCodeScanned
        );
      }
    },
    { immediate: true }
  );
}
</script>
